import { create } from 'zustand'
import type { InterviewSession } from '@/types/api'
import { interviewService } from '@/services'

interface ChatSessionsState {
  sessions: InterviewSession[]
  loading: boolean
  error: string | null

  /** Fetch all sessions from the API. */
  fetchSessions: () => Promise<void>

  /** Optimistically prepend a newly-created session. */
  addSession: (session: InterviewSession) => void

  /** Update a single session in-place (e.g. after receiving new messages). */
  updateSession: (id: string, patch: Partial<InterviewSession>) => void
}

export const useChatSessionsStore = create<ChatSessionsState>((set, get) => ({
  sessions: [],
  loading: false,
  error: null,

  fetchSessions: async () => {
    if (get().loading) return
    set({ loading: true, error: null })
    try {
      const sessions = await interviewService.listSessions()
      set({ sessions, loading: false })
    } catch (err) {
      console.error('Failed to fetch chat sessions:', err)
      set({ error: 'Failed to load sessions', loading: false })
    }
  },

  addSession: (session) =>
    set((state) => ({
      sessions: [session, ...state.sessions.filter((s) => s.id !== session.id)],
    })),

  updateSession: (id, patch) =>
    set((state) => ({
      sessions: state.sessions.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    })),
}))
